import styled from "@emotion/styled";
import { palette, typography } from "@jnpmedi/grebob";
import { defaultMarkerColor, getColorBySignerIndex, inputItemCss } from "@newComponents/common/Viewer/types";
import { useDocumentMarkerContext } from "@newHooks/context/document/documentMarker.context";
import { useEConsentContext } from "@newHooks/context/eConsent/eConsent.context";
import { fadeInBorderMarker } from "@assets/styles/animations";
import { EConsentMarkerProps } from "./EConsentMarker";
import { DocumentSignerAttrType, DocumentSignerInputItemCss } from "@maven-rest/docs/DocumentSign.dto";
import { JsonHelper } from "@maven-helper/helpers/Json.helper";

export function TextFieldMarker(props: EConsentMarkerProps) {
  const { markers, setMarkers } = useDocumentMarkerContext();
  const { signee } = useEConsentContext();

  const getMarkerType = (attrType: string) => {
    switch (attrType) {
      case DocumentSignerAttrType.AgentInputItemMark:
        return {
          marker: DocumentSignerAttrType.AgentInputItemMark,
          index: DocumentSignerAttrType.AgentInputItemIndex,
          missing: DocumentSignerAttrType.AgentInputItemMissing,
          value: DocumentSignerAttrType.AgentInputItemValue,
          regex: DocumentSignerAttrType.AgentInputItemRegex,
          css: DocumentSignerAttrType.AgentInputItemCss,
        };
      default:
        return {
          marker: DocumentSignerAttrType.InputItemMark,
          index: DocumentSignerAttrType.InputItemIndex,
          missing: DocumentSignerAttrType.InputItemMissing,
          value: DocumentSignerAttrType.InputItemValue,
          regex: DocumentSignerAttrType.InputItemRegex,
          css: DocumentSignerAttrType.InputItemCss,
        };
    }
  };

  const markerTypes = getMarkerType(props.marker.attr);

  const inputItemIndex = markers.find((e) => e.attr === markerTypes.index && e.attrUuid === props.marker.groupUuid);
  const inputItemMissing = markers.find((e) => e.attr === markerTypes.missing && inputItemIndex?.attrUuid === e.groupUuid);
  const inputItemValue = markers.find((e) => e.attr === markerTypes.value && inputItemIndex?.attrUuid === e.groupUuid);

  const inputItemStyleToString =
    markers.find((e) => e.attr === markerTypes.css && props.marker.groupUuid === e.groupUuid)?.value ??
    `${signee.attrs.find((e) => e.attr === markerTypes.css && props.marker.groupUuid === e.groupUuid)?.value ?? {}}`;

  const inputItemStyle: DocumentSignerInputItemCss = inputItemStyleToString
    ? (JsonHelper.tryParse(
        markers.find((e) => e.attr === markerTypes.css && props.marker.groupUuid === e.groupUuid)?.value ??
          `${signee.attrs.find((e) => e.attr === markerTypes.css && props.marker.groupUuid === e.groupUuid)?.value ?? {}}`
      ) as DocumentSignerInputItemCss)
    : {};

  const handleInputChange = (newValue: string) => {
    const updatedMarkers = markers
      .filter(
        (x) => !(x.attr === markerTypes.value && x.attrUuid === inputItemValue?.attrUuid && x.groupUuid === inputItemValue?.groupUuid)
      )
      .concat([
        {
          id: inputItemValue?.id,
          signerUuid: inputItemValue?.signerUuid!,
          target: inputItemValue?.target!,
          signerIndex: inputItemValue?.signerIndex!,
          attr: markerTypes.value,
          attrUuid: inputItemValue?.attrUuid!,
          groupUuid: inputItemValue?.groupUuid!,
          value: newValue,
          page: inputItemValue?.page!,
          x: inputItemValue?.x!,
          y: inputItemValue?.y!,
        },
      ]);
    setMarkers(updatedMarkers);
  };

  const handleClickTextFieldMarker = (e: React.MouseEvent<HTMLDivElement>) => {
    props.onClick?.(e);
  };

  const width = inputItemStyle?.width ?? inputItemCss?.width!;
  const height = inputItemStyle?.height ?? inputItemCss?.height!;

  return (
    <StyledTextFieldWrapper
      {...props}
      isHidden={props.isHidden}
      isFulfilled={!!inputItemValue?.value}
      resizeMode={props.resizeMode}
      width={width + "px"}
      height={height + "px"}
      onClick={handleClickTextFieldMarker}
    >
      {props.isModeSigning ? (
        <textarea
          style={{
            ...typography["Content.Subtitle04"],
            padding: "0px",
            color: !!inputItemValue?.value ? palette[defaultMarkerColor.additional.text] : palette["grey040"],
          }}
          value={inputItemValue?.value || ""}
          placeholder={inputItemMissing?.value || "Enter Text"}
          maxLength={256}
          onChange={(e) => handleInputChange(e.target.value)}
        />
      ) : (
        <div>{props.children}</div>
      )}
    </StyledTextFieldWrapper>
  );
}

const StyledTextFieldWrapper = styled("div")<
  EConsentMarkerProps & { isFulfilled: boolean; resizeMode: boolean; width: string; height: string }
>((props) => ({
  userSelect: "none",
  boxSizing: "border-box",
  display: props.isHidden ? "none" : "flex",
  alignItems: "center",
  position: "absolute",
  width: props.width,
  height: props.height,
  top: props.top,
  left: props.left,
  transform: "translateY(-50%)",
  borderRadius: "2px",
  border: `2px solid ${
    props.isModeSigning ? palette[defaultMarkerColor.additional.border] : palette[getColorBySignerIndex(props.marker.signerIndex).border]
  }`,
  padding: "1px",
  textIndent: 0,

  outline: "none",
  backgroundColor: props.isModeSigning
    ? defaultMarkerColor.additional.background
    : getColorBySignerIndex(props.marker.signerIndex).background,

  cursor: "pointer",

  "&:hover": {
    backgroundColor: props.isModeSigning
      ? defaultMarkerColor.additional.backgroundHover
      : getColorBySignerIndex(props.marker.signerIndex).backgroundHover,

    //NOTE: marker hover 시, UnlinkButton이 보임. 단, 리사이징 중에는 안보임.
    "& > div > span": {
      opacity: props.resizeMode ? 0 : 1,
    },
  },

  "&":
    !props.isFulfilled && props.isModeSigning
      ? {
          animation: `${fadeInBorderMarker} 1.6s ease-in-out infinite`,
        }
      : "",

  div: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  textarea: {
    resize: "none",
    width: "100%",
    height: "100%",
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    fontSize: "10px",
    lineHeight: "135%",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));
